import type { TextareaHTMLAttributes } from "react";
import { forwardRef, useEffect, useRef } from "react";
import classNames from "classnames/dedupe";
import type { DOMProps } from "@simplybusiness/mobius";

export type PromptInputTextareaElementType = HTMLTextAreaElement;
export type PromptInputTextareaRef = React.Ref<PromptInputTextareaElementType>;

export interface PromptInputTextareaProps
  extends
    Omit<DOMProps, "ref">,
    Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "className"> {
  /**
   * Custom class name
   */
  className?: string;
  /**
   * Whether the prompt input is expanded (shows 3 rows) or collapsed (shows 1 row)
   */
  isExpanded?: boolean;
  /**
   * Callback to trigger form submission (typically passed from parent form)
   */
  onSubmitRequest?: () => void;
}

const PromptInputTextarea = forwardRef<
  PromptInputTextareaElementType,
  PromptInputTextareaProps
>((props: PromptInputTextareaProps, ref: PromptInputTextareaRef) => {
  const {
    className,
    isExpanded = false,
    onSubmitRequest,
    ...otherProps
  } = props;
  const internalRef = useRef<HTMLTextAreaElement>(null);

  const classes = classNames("mobius-prompt-input__textarea", className);
  const rows = isExpanded ? 3 : 1;

  // Reset scroll position when collapsed to ensure only first line is visible
  useEffect(() => {
    const textarea = internalRef.current;
    if (textarea && !isExpanded) {
      textarea.scrollTop = 0;
    }
  }, [isExpanded, otherProps.value]);

  // Handle scroll events to prevent scrolling when collapsed
  const handleScroll = (event: React.UIEvent<HTMLTextAreaElement>) => {
    if (!isExpanded) {
      event.currentTarget.scrollTop = 0;
    }
    // Call original onScroll if provided
    if (otherProps.onScroll) {
      otherProps.onScroll(event);
    }
  };

  // Handle Enter key press for form submission
  const handleKeyDown = (event: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (event.key === "Enter" && !event.shiftKey) {
      const textarea = event.currentTarget;
      const hasText = textarea.value.trim().length > 0;

      if (hasText && onSubmitRequest) {
        event.preventDefault(); // Prevent new line
        onSubmitRequest();
      }
    }

    // Call original onKeyDown if provided
    if (otherProps.onKeyDown) {
      otherProps.onKeyDown(event);
    }
  };

  return (
    <textarea
      ref={node => {
        internalRef.current = node;
        if (typeof ref === "function") {
          ref(node);
        } else if (ref) {
          ref.current = node;
        }
      }}
      {...otherProps}
      className={classes}
      rows={rows}
      onScroll={handleScroll}
      onKeyDown={handleKeyDown}
    />
  );
});

PromptInputTextarea.displayName = "PromptInputTextarea";
export { PromptInputTextarea };
